import { useState, useRef, useEffect, useCallback } from "react";

type OvserveContent = {
  el: Element;
  observe: IntersectionObserver;
};

export const useVisibleTabs = () => {
  const leftBadgeIds = useRef<Set<string> | null>(null);
  if (leftBadgeIds.current === null) {
    leftBadgeIds.current = new Set();
  }
  const rightBadgeIds = useRef<Set<string> | null>(null);
  if (rightBadgeIds.current === null) {
    rightBadgeIds.current = new Set();
  }
  const [hasLeftBadge, setHasLeftBadge] = useState(false);
  const [hasRightBadge, setHasRightBadge] = useState(false);
  const tabAreaRef = useRef<HTMLDivElement | null>(null);
  const tabBadgeRefs = useRef<Record<string, OvserveContent | null>>({});

  const checkIntersection = useCallback(
    (id: string, element: Element, isIntersection?: boolean) => {
      if (!leftBadgeIds.current || !rightBadgeIds.current) return;
      const selectedRect = tabAreaRef.current
        ?.getElementsByClassName("MuiTabs-scroller")[0]
        ?.getBoundingClientRect();
      if (!selectedRect) return;

      const tabRect = element.getBoundingClientRect();
      const tabPosition = tabRect.right < selectedRect.left ? "left" : "right";
      const isBadgeInvisibled =
        element.classList.contains("MuiBadge-invisible");

      const isVisible =
        isIntersection ??
        (tabRect.right > selectedRect.left &&
          tabRect.left < selectedRect.right);

      if (!isVisible && !isBadgeInvisibled) {
        if (tabPosition === "left") {
          leftBadgeIds.current.add(id);
          rightBadgeIds.current.delete(id);
        } else {
          leftBadgeIds.current.delete(id);
          rightBadgeIds.current.add(id);
        }
      } else {
        leftBadgeIds.current.delete(id);
        rightBadgeIds.current.delete(id);
      }

      setHasLeftBadge(leftBadgeIds.current.size > 0);
      setHasRightBadge(rightBadgeIds.current.size > 0);
    },
    []
  );

  const registerIntersection = useCallback(
    (id: string) => {
      return new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          checkIntersection(id, entry.target, entry.isIntersecting);
        });
      });
    },
    [checkIntersection]
  );

  const disconnectTab = useCallback((id: string) => {
    const beforeBadge = tabBadgeRefs.current[id];
    if (beforeBadge) {
      if (leftBadgeIds.current) {
        leftBadgeIds.current.delete(id);
        setHasLeftBadge(leftBadgeIds.current.size > 0);
      }
      if (rightBadgeIds.current) {
        rightBadgeIds.current.delete(id);
        setHasRightBadge(rightBadgeIds.current.size > 0);
      }
      beforeBadge.observe.disconnect();
      tabBadgeRefs.current[id] = null;
      return;
    }
  }, []);

  const registerTab = useCallback(
    (id: string, ref: HTMLSpanElement | null) => {
      if (!ref) return;
      const content = ref.getElementsByClassName("MuiBadge-badge")[0];
      if (!content) {
        disconnectTab(id);
        return;
      }
      const isInvisible = content.classList.contains("MuiBadge-invisible");
      const beforeBadge = tabBadgeRefs.current[id];

      // 通知がない場合は、BadgeIdsから削除し監視を解除
      if (isInvisible) {
        disconnectTab(id);
        return;
      }

      // 通知がある場合は表示を監視
      if (!beforeBadge) {
        const observeObject = registerIntersection(id);
        observeObject.observe(content);
        tabBadgeRefs.current[id] = {
          el: content,
          observe: observeObject,
        };
      }
    },
    [registerIntersection, disconnectTab]
  );

  const refreshOvserve = useCallback(() => {
    const refs = tabBadgeRefs.current;
    Object.entries(refs).forEach(([id, current]) => {
      if (!current) return;
      if (!current.el.isConnected) {
        disconnectTab(id);
        return;
      }

      checkIntersection(id, current.el);
    });
  }, [disconnectTab, checkIntersection]);

  useEffect(() => {
    const tabBadgeRefsCurrent = tabBadgeRefs.current;
    return () => {
      Object.keys(tabBadgeRefsCurrent).forEach((key) =>
        tabBadgeRefsCurrent[key]?.observe.disconnect()
      );
    };
  }, []);

  return {
    hasLeftBadge,
    hasRightBadge,
    registerTab,
    disconnectTab,
    tabAreaRef,
    refreshOvserve,
  };
};
